/**
 * <size-unit-toggle>
 * Wall-art only. Lets the customer switch the print size labels between inches
 * and centimeters. The inch value is the real variant option (never changed);
 * cm is a display-only alternative sourced from the `size_conversion` metaobject.
 *
 * - Relabels the size option buttons live (survives variant-picker morphs).
 * - Restates the millimetre measurements in every spec string marked
 *   `[data-unit-convert]` as inches, so the frame subtitle, the "The canvas" /
 *   "The frame" tiles and the Materials/Framing accordion rows don't sit in
 *   metric next to an inch size row.
 * - Shows the sticky add-to-cart bar's size in the same unit (it survives that
 *   bar's own morphs too — the server can only render the inch form).
 * - Persists the choice in localStorage.
 * - Keeps two line item properties in sync (hidden inputs associated to the
 *   product form): a visible one carrying BOTH units, which is the only place a
 *   metric size reaches checkout and the order emails (the variant itself is
 *   named in inches only), and `_size_unit`, which records which unit the
 *   shopper was reading so the cart line can lead with it.
 */
(() => {
  'use strict';

  const STORAGE_KEY = 'binspired_size_unit';

  class SizeUnitToggle extends HTMLElement {
    connectedCallback() {
      this.map = this._readMap();
      this.unit = this._readUnit();
      this.propInput = this.querySelector('.size-unit-toggle__prop');
      this.unitPropInput = this.querySelector('.size-unit-toggle__unit-prop');
      this._buttonsGroup = this.querySelector('.size-unit-toggle__buttons');
      this._selectedEl = this.querySelector('.size-unit-toggle__selected');

      // Per-text-node record of what each spec string was converted from; see
      // `_convertSpec`. Weak so a morphed-away caption's nodes are collectable.
      this._specSources = new WeakMap();

      this._onMutation = this._onMutation.bind(this);

      this._renderButtons();
      this._wire();
      this._apply();
      this._startObserve();
    }

    disconnectedCallback() {
      this._stopObserve();
    }

    /* ---------- data ---------- */

    _readMap() {
      try {
        return JSON.parse(this.querySelector('.size-unit-toggle__data')?.textContent || '{}');
      } catch {
        return {};
      }
    }

    _readUnit() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === 'in' || stored === 'cm') return stored;
      } catch {}
      return this.dataset.defaultUnit === 'cm' ? 'cm' : 'in';
    }

    _writeUnit(unit) {
      try {
        localStorage.setItem(STORAGE_KEY, unit);
      } catch {}
    }

    /** The variant picker that owns the size options, scoped to this section. */
    get _picker() {
      const scope = this.closest('.shopify-section') || document;
      return scope.querySelector('variant-picker');
    }

    /* ---------- UI ---------- */

    /**
     * The in/cm buttons. Queried from their group (not the host) because
     * `_positionControl` relocates the group out of this element and into the
     * Size legend — after which `this.querySelectorAll` would find nothing.
     */
    _buttons() {
      return (this._buttonsGroup || this).querySelectorAll('[data-unit]');
    }

    _renderButtons() {
      this._buttons().forEach((btn) => {
        const on = btn.dataset.unit === this.unit;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    _wire() {
      this._buttons().forEach((btn) => {
        btn.addEventListener('click', () => {
          const next = btn.dataset.unit;
          if (next === this.unit) return;
          this.unit = next;
          this._writeUnit(next);
          this._renderButtons();
          this._apply();
        });
      });
    }

    /** The fieldset/wrapper that holds the size options, found via the cm map. */
    get _sizeContainer() {
      const picker = this._picker;
      if (!picker) return null;
      const node = Array.from(picker.querySelectorAll('input[type="radio"], select')).find((el) => {
        if (el.tagName === 'SELECT') {
          return Array.from(el.options).some((o) =>
            Object.prototype.hasOwnProperty.call(this.map, (o.value || '').trim())
          );
        }
        return Object.prototype.hasOwnProperty.call(this.map, (el.value || '').trim());
      });
      return node ? node.closest('fieldset, .variant-option') : null;
    }

    /**
     * The `_size-guide` block's link, if that block is on the page. It rides
     * along into the Size legend rather than sitting under the frame swatches,
     * so the row reads: SIZE · <value> ..... Size guide [in|cm]. Docking it
     * from here (rather than from its own block) keeps one script in charge of
     * the row's order.
     *
     * Cached on first find, exactly as `_buttonsGroup` and `_selectedEl` are,
     * and for the same reason: a picker morph replaces the legend and takes
     * everything docked into it with it. The size-guide block does not re-render
     * alongside the picker, so re-querying the DOM afterwards finds nothing —
     * this reference is the only remaining handle on the link, and re-appending
     * it is what puts it back.
     */
    get _sizeGuideTrigger() {
      if (this._sizeGuideEl) return this._sizeGuideEl;
      const scope = this.closest('.shopify-section') || document;
      this._sizeGuideEl = scope.querySelector('.size-guide__trigger');
      // Grab the wrapper now too — once the link is docked it is no longer a
      // descendant, so `closest()` could not find it later.
      this._sizeGuideWrapper = this._sizeGuideEl?.closest('.size-guide') ?? null;
      return this._sizeGuideEl;
    }

    /**
     * Dock the in/cm buttons — and the size-guide link, when present — onto the
     * Size option's label row so they sit to the right of the "Size" legend (no
     * separate "Units" label needed).
     */
    _positionControl() {
      const group = this._buttonsGroup;
      const container = this._sizeContainer;
      if (!group || !container) return;

      const target = container.querySelector('legend') || container.querySelector(':scope > label') || container;
      target.classList.add('size-unit-toggle-host');

      const trigger = this._sizeGuideTrigger;
      // The link's own wrapper is left holding nothing but the (closed) dialog,
      // so collapse it out of the column it used to occupy.
      if (trigger) this._sizeGuideWrapper?.classList.add('size-guide--docked');

      // Keep order after the "Size" label: readout, size-guide link, buttons
      // last. A picker morph can strip any of them, so re-append all three (in
      // order) whenever one is out.
      const placed =
        group.parentElement === target &&
        (!this._selectedEl || this._selectedEl.parentElement === target) &&
        (!trigger || trigger.parentElement === target);
      if (!placed) {
        if (this._selectedEl) target.appendChild(this._selectedEl);
        if (trigger) target.appendChild(trigger);
        target.appendChild(group);
      }
    }

    /* ---------- core ---------- */

    /** Iterate every size option label, calling cb(textEl, inchValue). */
    _forEachSizeLabel(cb) {
      const picker = this._picker;
      if (!picker) return;

      picker.querySelectorAll('input[type="radio"]').forEach((input) => {
        const value = (input.value || '').trim();
        if (!Object.prototype.hasOwnProperty.call(this.map, value)) return;
        const label = input.closest('label');
        const textEl = label && label.querySelector('[data-key="variant-option-text"]');
        if (textEl) cb(textEl, input.value);
      });

      picker.querySelectorAll('select option').forEach((option) => {
        const value = (option.value || '').trim();
        if (!Object.prototype.hasOwnProperty.call(this.map, value)) return;
        cb(option, option.value);
      });
    }

    /**
     * Display form of a size value, in either unit: `9" × 12"` → `9 × 12 in`,
     * `23×30 cm` → `23 × 30 cm`. A square ladder writes a single measurement
     * (`12"`, `30 cm`); it is repeated so the label reads like every other rung.
     * No source value is rewritten — they are only relabelled, so both units read
     * the same way. Mirrors snippets/size-label.liquid, which renders the same
     * form server-side.
     */
    _sizeLabel(value, compact) {
      const raw = (value || '').trim();
      if (!raw) return '';
      const isInches = /["\u201d\u2033]/.test(raw);
      const isCm = /cm/i.test(raw);
      let label = raw
        .replace(/["\u201d\u2033]/g, '')
        .replace(/cm/gi, '')
        .replace(/\s*×\s*/g, ' × ')
        .trim();
      if (!label.includes('×') && (isInches || isCm)) label = `${label} × ${label}`;
      if (isInches) label += ' in';
      else if (isCm) label += ' cm';
      return compact ? label.replace(/\s*×\s*/g, '×') : label;
    }

    _relabel() {
      const toCm = this.unit === 'cm';
      this._forEachSizeLabel((textEl, inchValue) => {
        const source = toCm ? this.map[inchValue.trim()] || inchValue : inchValue;
        const next = this._sizeLabel(source);
        if (textEl.textContent !== next) textEl.textContent = next;
      });
    }

    _selectedSizeValue() {
      const picker = this._picker;
      if (!picker) return null;
      const checked = Array.from(
        picker.querySelectorAll('input[type="radio"]:checked, select option:checked')
      )
        .map((el) => (el.value || '').trim())
        .find((v) => Object.prototype.hasOwnProperty.call(this.map, v));
      return checked || null;
    }

    _updateProperty() {
      const selected = this._selectedSizeValue();

      if (this.propInput) {
        // Both units, every time — regardless of which one is on screen. This property is
        // what the customer sees at checkout and in the order email, where the variant is
        // named in inches alone.
        const inches = this._sizeLabel(selected);
        const cm = selected ? this._sizeLabel(this.map[selected]) : '';
        this.propInput.value = inches && cm ? `${inches} (${cm})` : inches;
        this.propInput.disabled = !inches; // disabled inputs are not submitted
      }

      if (this.unitPropInput) this.unitPropInput.value = this.unit;
    }

    /** Show the selected size (in the active unit) as "SIZE · <value>". */
    _updateValue() {
      if (!this._selectedEl) return;
      const selected = this._selectedSizeValue();
      const display = selected
        ? this._sizeLabel(this.unit === 'cm' ? this.map[selected] || selected : selected)
        : null;
      const valEl = this._selectedEl.querySelector('.size-unit-toggle__val');
      if (display && valEl) {
        valEl.textContent = display.trim();
        this._selectedEl.hidden = false;
      } else {
        this._selectedEl.hidden = true;
      }
    }

    /* ---------- spec strings ---------- */

    /**
     * The spec strings that follow the unit toggle, marked `data-unit-convert` in
     * Liquid: the selected frame's construction subtitle on the Frame row, the
     * "The canvas" / "The frame" tiles below the buy buttons, and any accordion
     * row switched to "Convert measurements…".
     *
     * Queried from the document, not from this section: the product's detail
     * accordion is a section of its own. That is safe because nothing is converted
     * unless Liquid marked it.
     */
    _specElements() {
      return document.querySelectorAll('[data-unit-convert]');
    }

    /**
     * Metric measurements → inches: "400gsm cotton canvas, 38mm deep" becomes
     * "400gsm cotton canvas, 1.5in deep". One decimal, trailing zero dropped, which
     * is exactly how the supplier writes the one depth it publishes in both units
     * (38mm = 1.5" canvas wrap). Anything that isn't a measurement — gsm, "Giclée
     * inks" — is left untouched, and so is a value already written in inches.
     */
    _toInches(text) {
      return text.replace(/(\d+(?:[.,]\d+)?)\s*(mm|cm)\b/gi, (match, amount, unit) => {
        const mm = parseFloat(amount.replace(',', '.')) * (unit.toLowerCase() === 'cm' ? 10 : 1);
        if (!Number.isFinite(mm)) return match;
        return `${Math.round((mm / 25.4) * 10) / 10}in`;
      });
    }

    /**
     * Rewrite one marked element, text node by text node, so rich text keeps its
     * markup — an accordion row is a paragraph with emphasis and links in it, not
     * a string.
     *
     * `_specSources` remembers, per text node, the metric wording it was converted
     * from and the text we last wrote from it. A node whose text is no longer what
     * we wrote has been re-rendered by the server since — a variant change morphs
     * the frame subtitle to the newly selected frame's copy — so its current text
     * is the fresh metric source. That one comparison is what stops a second pass
     * from converting inches again, and what stops a stale source from outliving
     * the caption it belonged to.
     */
    _convertSpec(root) {
      const toInches = this.unit === 'in';
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        let entry = this._specSources.get(node);
        if (!entry || entry.rendered !== node.nodeValue) entry = { source: node.nodeValue };
        const next = toInches ? this._toInches(entry.source) : entry.source;
        if (node.nodeValue !== next) node.nodeValue = next;
        this._specSources.set(node, { source: entry.source, rendered: next });
      }
    }

    _updateSpecs() {
      this._specElements().forEach((el) => this._convertSpec(el));
    }

    /** The sticky add-to-cart bar of this section, if the bar is enabled. */
    get _stickyBar() {
      const scope = this.closest('.shopify-section') || document;
      return scope.querySelector('sticky-add-to-cart');
    }

    /** That bar's size text. Absent while the bar has no size to show. */
    get _stickySize() {
      return this._stickyBar?.querySelector('[data-sticky-size]') || null;
    }

    /**
     * Show the sticky bar's size in the unit on screen, and only that one — the bar
     * restates a choice the shopper just made, so the second unit would be noise on
     * the row that has the least space for it. Both units stay on the element as
     * data attributes; the server can only render the inch form, since the unit is
     * a client-side choice.
     */
    _updateSticky() {
      const el = this._stickySize;
      if (!el) return;

      const inches = el.dataset.sizeIn || '';
      const cm = el.dataset.sizeCm || '';
      if (!inches && !cm) return;

      const next = this.unit === 'cm' ? cm || inches : inches || cm;
      if (el.textContent !== next) el.textContent = next;
    }

    _apply() {
      // Pause observation so our own writes don't retrigger us.
      this._stopObserve();
      this._positionControl();
      this._relabel();
      this._updateProperty();
      this._updateValue();
      this._updateSpecs();
      this._updateSticky();
      this._startObserve();
    }

    /* ---------- morph resilience ---------- */

    _startObserve() {
      const picker = this._picker;
      const sticky = this._stickyBar;
      if (!picker && !sticky) return;
      if (!this._observer) this._observer = new MutationObserver(this._onMutation);
      if (picker) {
        this._observer.observe(picker, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['data-current-checked', 'value'],
        });
      }
      // The sticky bar re-renders from the server on every variant change, which
      // restores the inch-led text and swaps in a fresh span. Watched separately
      // because it morphs on its own schedule, not with the picker.
      if (sticky) {
        this._observer.observe(sticky, { childList: true, subtree: true });
      }
    }

    _stopObserve() {
      if (this._observer) this._observer.disconnect();
    }

    _onMutation() {
      // A variant-picker or sticky-bar morph reset the labels; re-apply.
      this._apply();
    }
  }

  if (!customElements.get('size-unit-toggle')) {
    customElements.define('size-unit-toggle', SizeUnitToggle);
  }
})();
